import { useEffect } from 'react';
import { proStudioUrqlGql } from '@pypestream/api-services';

import { toDeploymentProgressRows } from '../utils';
import { selectOpenSolutionId } from '../utils/select-open-solution-id';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { reportObserverFailure } from '../xstate/report-observer-failure';

/**
 * Keeps `ctx.solutionDeployments` in sync with the open solution's deployment rows.
 *
 * One subscription for the whole app, over the same `graphql-ws` socket everything else
 * uses, feeding both the publish toast and the Versions environment cards — a publish is one
 * event and every viewer must see the same thing, so it would be wrong for two consumers to
 * hold two independently-timed copies of it.
 *
 * Mounted once, in the router root: publishing is announced wherever the user happens to be
 * (workflows, files, channels…), not only on the Versions page.
 *
 * Keyed on the route's solution, not `selectedBot`, which every publish reload clears — the
 * rows describing a publish must not be wiped and resubscribed in the middle of it
 * (PSTUDIO-4333). See `selectOpenSolutionId`.
 */
export function useSolutionDeployments(): void {
  const solutionId = useProStudioCtxSelector(selectOpenSolutionId);

  useEffect(() => {
    if (!solutionId) return undefined;

    const { subscribe } = proStudioUrqlGql.solutionDeploymentsSubscription({
      solutionId,
    });

    const { unsubscribe } = subscribe((subData) => {
      if (subData.error) {
        // PSTUDIO-4333 B5: reported, no longer silent. The last rows are deliberately kept:
        // cleared, the publish watcher would lose the attempt it follows and read the lock's
        // release as a success, and the Versions cards would go blank. A row left mid-publish
        // is ended by the watcher's lock-expiry timer.
        reportObserverFailure(subData.error, 'publish-watcher:deployments');
        return;
      }
      const rows = subData.data?.tenancy_solution_deployments;
      if (!rows) return;
      sendProStudioEvent({
        type: 'setSolutionDeployments',
        deployments: toDeploymentProgressRows(rows),
      });
    });

    return () => {
      unsubscribe();
      // Clear on the way out: the next solution's rows arrive a round trip later, and until
      // they do, the cards must not show the previous solution's publish state.
      sendProStudioEvent({ type: 'setSolutionDeployments', deployments: [] });
    };
  }, [solutionId]);
}
